/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { YmmeAcesVehicleData } from '../interface';

export type PreferredVehicleVinMileage = {
  vin: string | null;
  mileage: number | null;
};

/**
 * Extracts VIN and mileage from the user's preferred vehicle.
 *
 * Finds the vehicle marked as preferred in ACES data and returns its VIN and mileage.
 * VIN is sanitized (trimmed, empty strings converted to null) and mileage is parsed
 * from string format (removing commas) to a number.
 *
 * @param acesVehiclesData - ACES vehicles data containing user's saved vehicles
 * @returns Object with sanitized VIN (string | null) and parsed mileage (number | null).
 *          Returns null for both if no preferred vehicle exists or values are invalid.
 */
export const getPreferredVehicleVinMileage = (
  acesVehiclesData: YmmeAcesVehicleData | undefined,
): PreferredVehicleVinMileage => {
  const preferredVehicle = acesVehiclesData?.vehiclesInfo.find(
    (vehicle) => vehicle.preferredVehicle === true,
  );

  const rawVin = preferredVehicle?.vin;
  const sanitizedVin = rawVin && typeof rawVin === 'string' ? rawVin.trim() : null;
  const vin = sanitizedVin && sanitizedVin.length > 0 ? sanitizedVin : null;

  const rawMileage = preferredVehicle?.estimatedMileage;
  const cleanedMileage = rawMileage ? String(rawMileage).replace(/,/g, '') : '';
  const parsedMileage = cleanedMileage ? parseInt(cleanedMileage, 10) : NaN;
  const mileage = !isNaN(parsedMileage) && isFinite(parsedMileage) ? parsedMileage : null;

  return {
    vin,
    mileage,
  };
};
